import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { SvgIconComponent } from '@mui/icons-material';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MapIcon from '@mui/icons-material/Map';
import FlagIcon from '@mui/icons-material/Flag';
import PublicIcon from '@mui/icons-material/Public';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';
import type { Brand } from '@traaker/shared';
import { resolveEntitlement } from '@traaker/shared';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';

/**
 * Onboarding-style editor for a brand's prompt languages + geographic zones,
 * usable on the Brand Knowledge page. Mirrors `BrandOnboarding` PromptsStep:
 *   - languages as flag chips (toggle),
 *   - zones as scale-icon tiles fetched from POST /infer-zones, plus a manual
 *     add and the geography-agnostic "Global" pseudo-zone,
 *   - tenant-wide quota display + enforcement for non-agency (hidden for agency),
 *   - min 1 language and min 1 zone (Global satisfies the zone minimum).
 *
 * All edits persist IMMEDIATELY (like onboarding): zones via the locations API,
 * languages via PATCH /strategy followed by an auto re-validate of Brand + GEO
 * (changing targetLanguages flips both metas to stale, which would block the
 * audit gate). These directly drive the next prompt generation.
 *
 * The server is the load-bearing quota gate (zones gauge + languages union);
 * the UI checks here are purely UX.
 */

const PROMPT_LANGUAGES: { code: string; label: string; flag: string }[] = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
];

type ZoneScale = 'village' | 'ville' | 'region' | 'pays' | 'europe' | 'monde';
const SCALE_ICON: Record<ZoneScale, SvgIconComponent> = {
  village: HolidayVillageIcon,
  ville: LocationCityIcon,
  region: MapIcon,
  pays: FlagIcon,
  europe: PublicIcon,
  monde: LanguageIcon,
};

interface InferredZone {
  name: string;
  scale: ZoneScale;
  countryCode?: string;
}

// "Global" = no geographic anchor. Never persisted as a location; satisfies the
// "min 1 zone" rule and is exempt from the zones quota.
const GLOBAL_ZONE = 'Global';

export default function LanguageZoneEditor({ brand }: { brand: Brand }) {
  const { t } = useTranslation();
  const { tenant } = useAuth();
  const { refreshBrands } = useBrand();
  const isAgency = tenant?.tier === 'agency';

  const zonesLimit = tenant
    ? resolveEntitlement('zones', tenant.tier, {
        override: tenant.settings?.quotaOverrides?.zones,
        packs: tenant.settings?.activePacks?.zones,
      }).limit
    : -1;
  const languagesLimit = tenant
    ? resolveEntitlement('languages', tenant.tier, {
        override: tenant.settings?.quotaOverrides?.languages,
        packs: tenant.settings?.activePacks?.languages,
      }).limit
    : -1;

  // Existing persisted real zones (locations) for THIS brand.
  const existingLocations = brand.locations || [];
  const realZoneCount = existingLocations.length;
  // Quota is tenant-wide; for the cap check we'd need all brands, but the server
  // is the real gate. Here we surface this brand's count vs the limit as the
  // primary signal (matches onboarding's per-flow display).
  const zonesAtCap = !isAgency && zonesLimit !== -1 && realZoneCount >= zonesLimit;

  const languages = (brand.strategy?.targetLanguages || [])
    .map((l) => (l || '').trim().toLowerCase())
    .filter(Boolean);
  const languagesAtCap = !isAgency && languagesLimit !== -1 && languages.length >= languagesLimit;

  const [inferredZones, setInferredZones] = useState<InferredZone[]>([]);
  const [zonesLoading, setZonesLoading] = useState(true);
  const [zoneInput, setZoneInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setZonesLoading(true);
    api
      .post<{ zones: InferredZone[] }>(`/brands/${brand.brandId}/infer-zones`, {})
      .then((res) => {
        if (cancelled) return;
        setInferredZones((res.zones || []).filter((z) => z?.name && SCALE_ICON[z.scale]));
      })
      .catch(() => void 0)
      .finally(() => {
        if (!cancelled) setZonesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [brand.brandId]);

  const refresh = useCallback(async () => {
    await refreshBrands();
  }, [refreshBrands]);

  // ── Languages ──
  const saveLanguages = async (next: string[]) => {
    if (busy) return;
    // Enforce min 1 language.
    if (next.length === 0) {
      setError(t('languageZoneEditor.errorMinLanguage'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/brands/${brand.brandId}/strategy`, { targetLanguages: next });
      // Changing targetLanguages flips Brand + GEO metas to stale — re-validate
      // so audits aren't blocked (best-effort, mirrors onboarding).
      try {
        await api.post(`/brands/${brand.brandId}/strategy/validate`, {});
        await api.post(`/brands/${brand.brandId}/geo-strategy/validate`, {});
      } catch {
        // Brand/GEO may not be validatable yet — the strategy banner handles it.
      }
      await refresh();
    } catch (err: any) {
      setError(err?.message || t('languageZoneEditor.errorSaveLanguages'));
    } finally {
      setBusy(false);
    }
  };

  const toggleLanguage = (code: string) => {
    const has = languages.includes(code);
    if (!has && languagesAtCap) return;
    saveLanguages(has ? languages.filter((c) => c !== code) : [...languages, code]);
  };

  // ── Zones ──
  const addZone = async (name: string, scale?: ZoneScale, countryCode?: string) => {
    const z = name.trim();
    if (!z || busy) return;
    if (existingLocations.some((l) => l.zone === z || l.label === z)) return;
    if (zonesAtCap) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/brands/${brand.brandId}/locations`, {
        label: z,
        zone: z,
        ...(countryCode && { countryCode }),
        ...(scale && { scale }),
      });
      await refresh();
      setZoneInput('');
    } catch (err: any) {
      setError(err?.message || t('languageZoneEditor.errorAddZone'));
    } finally {
      setBusy(false);
    }
  };

  const removeZone = async (locationId: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/brands/${brand.brandId}/locations/${locationId}`);
      await refresh();
    } catch (err: any) {
      setError(err?.message || t('languageZoneEditor.errorRemoveZone'));
    } finally {
      setBusy(false);
    }
  };

  // Tiles = inferred zones NOT already added (added ones render as removable
  // chips below). Global is shown as an always-on, non-actionable indicator when
  // there are no real locations (it's the implicit fallback).
  const addedZoneNames = new Set(existingLocations.map((l) => l.zone));
  const suggestionTiles = inferredZones.filter((z) => !addedZoneNames.has(z.name));

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Zones ── */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600}>
          {t('languageZoneEditor.zonesTitle')}
        </Typography>
        {!isAgency && zonesLimit !== -1 && (
          <Typography variant="caption" color={zonesAtCap ? 'error.main' : 'text.secondary'}>
            {t('languageZoneEditor.zonesCount', { used: realZoneCount, limit: zonesLimit })}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {t('languageZoneEditor.zonesHelp')}
      </Typography>

      {/* Added zones (+ Global indicator when no real location). */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Tooltip title={t('languageZoneEditor.globalTooltip')}>
          <Chip
            icon={<LanguageIcon />}
            label={GLOBAL_ZONE}
            color="primary"
            variant={realZoneCount === 0 ? 'filled' : 'outlined'}
            size="small"
          />
        </Tooltip>
        {existingLocations.map((loc) => {
          const scale = (loc.scale as ZoneScale) || 'ville';
          const Icon = SCALE_ICON[scale] || LocationCityIcon;
          return (
            <Chip
              key={loc.locationId}
              icon={<Icon />}
              label={loc.zone || loc.label}
              onDelete={busy ? undefined : () => removeZone(loc.locationId)}
              deleteIcon={<DeleteOutlineIcon />}
              size="small"
            />
          );
        })}
      </Box>

      {/* Inferred suggestions (scale icons). */}
      {zonesLoading ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mb: 2 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={40} />
          ))}
        </Box>
      ) : suggestionTiles.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1,
            mb: 1.5,
          }}
        >
          {suggestionTiles.map((z) => {
            const blocked = zonesAtCap || busy;
            const Icon = SCALE_ICON[z.scale] || LocationCityIcon;
            return (
              <Box
                key={z.name}
                onClick={() => !blocked && addZone(z.name, z.scale, z.countryCode)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1.5,
                  cursor: blocked ? 'not-allowed' : 'pointer',
                  opacity: blocked ? 0.5 : 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Checkbox checked={false} disabled={blocked} size="small" sx={{ p: 0.5 }} />
                <Icon sx={{ fontSize: 20, color: 'action.active' }} />
                <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
                  {z.name}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : null}

      {/* Manual add */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <TextField
          value={zoneInput}
          onChange={(e) => setZoneInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addZone(zoneInput);
            }
          }}
          fullWidth
          size="small"
          placeholder={t('languageZoneEditor.zonePlaceholder')}
          disabled={zonesAtCap || busy}
        />
        <Button
          variant="outlined"
          onClick={() => addZone(zoneInput)}
          disabled={!zoneInput.trim() || zonesAtCap || busy}
          sx={{ minWidth: 44, px: 0 }}
        >
          <AddIcon fontSize="small" />
        </Button>
      </Box>

      {/* ── Languages ── */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          {t('languageZoneEditor.languagesTitle')}
        </Typography>
        {!isAgency && languagesLimit !== -1 && (
          <Typography variant="caption" color={languagesAtCap ? 'error.main' : 'text.secondary'}>
            {t('languageZoneEditor.languagesCount', { used: languages.length, limit: languagesLimit })}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {t('languageZoneEditor.languagesHelp')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {PROMPT_LANGUAGES.map((l) => {
          const selected = languages.includes(l.code);
          const blocked = (!selected && languagesAtCap) || busy;
          return (
            <Chip
              key={l.code}
              label={`${l.flag}  ${l.label}`}
              onClick={() => !blocked && toggleLanguage(l.code)}
              disabled={blocked}
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
            />
          );
        })}
        {busy && <CircularProgress size={16} sx={{ ml: 1 }} />}
      </Box>
    </Box>
  );
}
